import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { noop, Subject } from 'rxjs';
import { BaseCoreState, baseDocumentActions } from '../../../_store';
import { BaseModalService } from '../../../shared/services/base-modal.service';
import { DocumentFormInterface } from '../../_interfaces/base-document-uploadmodal.interface';
import { BaseDocumentFormInterface } from '../../../modules/administration/_interfaces/base-master-data.interface';
import { BaseFileDownloadService } from '../../../shared/services/base-file-download.service';

type DocumentFormType = FormGroup<DocumentFormInterface> | FormGroup<BaseDocumentFormInterface>;

@Component({
  selector: 'base-document-upload-modal',
  templateUrl: './base-document-upload-modal.component.html',
})
export class BaseDocumentUploadModalComponent implements OnInit, OnDestroy {
  @ViewChild('documentViewerTemplate') documentViewerTemplate: ElementRef;
  @Input() documentUploadForm: DocumentFormType;
  @Input() submitted = false;
  @Input() isFileExisting = false;
  @Input() fixedCategory = false;
  @Input() documentName = 'document';
  @Input() hideExpirationDateSelection = false;
  @Input() hideNameInputField = false;
  @Input() documentCategories: { value: number; label: string }[] = [];
  @Input() documentNameInputPlaceholder = 'document.namePlaceholder';
  @Input() fileTypePlaceholder = 'document.dragDrop.fileTypes';
  @Output() openExistingDocument = new EventEmitter<boolean>();

  isValidIndefinitely = false;
  activeDocument: File;
  documentViewerModalRef: NgbModalRef;

  private destroy$ = new Subject<boolean>();

  constructor(
    private store: Store<BaseCoreState>,
    private modalService: BaseModalService,
    private fileDownloadService: BaseFileDownloadService
  ) {}

  get fileName() {
    const { name, file } = this.documentUploadForm.getRawValue();
    return file?.name || this.documentName || name || 'document';
  }

  ngOnInit(): void {
    this.isValidIndefinitely = !this.documentUploadForm.controls.validUntil.value;
    this.onValidIndefinitelyChange(this.isValidIndefinitely);
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  onValidIndefinitelyChange(isChecked: boolean): void {
    this.isValidIndefinitely = isChecked;
    const validUntilControl = this.documentUploadForm.controls.validUntil;
    if (this.isValidIndefinitely) {
      validUntilControl.setValue(null);
      validUntilControl.clearValidators();
      validUntilControl?.disable();
    } else {
      validUntilControl?.enable();
      validUntilControl.setValidators(Validators.required);
      validUntilControl.setValue(validUntilControl.value ? validUntilControl.value : '');
    }
  }

  deleteExistingFile(): void {
    const fileControl = this.documentUploadForm.controls.file;

    fileControl.reset();
    fileControl?.addValidators(Validators.required);
    fileControl?.updateValueAndValidity();
  }

  viewDocument(isDocumentExist?: boolean): void {
    if (isDocumentExist) {
      this.openExistingDocument.emit(isDocumentExist);
    } else {
      const file = this.documentUploadForm.getRawValue().file;

      if (file) {
        this.openDocumentViewer(file as File);
      }
    }
  }

  downloadImage() {
    if (!this.activeDocument) {
      return;
    }

    this.fileDownloadService.triggerBrowserDownload(this.activeDocument, this.fileName, this.activeDocument.type);
  }

  private openDocumentViewer(document: File | null): void {
    if (!document) {
      return;
    }

    this.activeDocument = document;

    this.documentViewerModalRef = this.modalService.open(
      {
        bodyTemplate: this.documentViewerTemplate,
        cancelButtonDisplayed: false,
        okButtonDisplayed: false,
        hideHeader: true,
        hideFooter: true,
      },
      { size: 'xl' }
    );
    this.documentViewerModalRef.result.then(() => {
      this.store.dispatch(baseDocumentActions.clearDownloadDocument());
    }, noop);
  }
}
